import { NormalizedSurveyData, ChartData, OptimismData, SummaryStats, ProcessedData } from '../types/survey';

export function processNormalizedSurveyData(data: NormalizedSurveyData[]): ProcessedData {
  // Calculate summary statistics
  const summaryStats = calculateNormalizedSummaryStats(data);
  
  // Process organization types
  const organizationTypes = processNormalizedOrganizationTypes(data);
  
  // Process services provided
  const servicesProvided = processNormalizedServicesProvided(data);
  
  // Process optimism by organization type
  const optimismByOrgType = processNormalizedOptimismByOrgType(data);
  
  // Process optimism totals for 18 months and 5 years
  const optimism18mo = processNormalizedOptimism18mo(data);
  const optimism5yr = processNormalizedOptimism5yr(data);
  
  // Process indirect effects
  const indirectEffects = processNormalizedIndirectEffects(data);
  
  // Process budget reduction
  const budgetReduction = processNormalizedBudgetReduction(data);
  
  // Process AI usage
  const aiUsage = processNormalizedAIUsage(data);
  
  // Process technology adoption
  const technologyAdoption = processNormalizedTechnologyAdoption(data);
  
  // Process service impacts
  const serviceImpacts = processNormalizedServiceImpacts(data);
  
  // Process adaptations
  const adaptations = processNormalizedAdaptations(data);
  
  // Process tech barriers
  const techBarriers = processNormalizedTechBarriers(data);
  
  // Process funding associated agencies
  const fundingAssociatedAgencies = processNormalizedFundingAssociatedAgencies(data);
  
  return {
    summaryStats,
    organizationTypes,
    servicesProvided,
    optimismByOrgType,
    optimism18mo,
    optimism5yr,
    indirectEffects,
    budgetReduction,
    aiUsage,
    technologyAdoption,
    serviceImpacts,
    adaptations,
    techBarriers,
    fundingAssociatedAgencies,
  };
}

function calculateNormalizedSummaryStats(data: NormalizedSurveyData[]): SummaryStats {
  const totalOrganizations = data.length;
  
  // Calculate total lost funding
  const totalLostFunding = data.reduce((sum, row) => {
    const fundingStr = row.funding_lost;
    if (!fundingStr || fundingStr === 'Unknown' || fundingStr === '') return sum;
    
    // Extract numbers from various forats
    const numbers = fundingStr.match(/[\d,]+/g);
    if (!numbers) return sum;
    
    const amount = numbers[0].replace(/,/g, '');
    const val = parseFloat(amount);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  
  const formattedFunding = totalLostFunding.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  
  // Calculate percentage with cancelled contracts
  const cancelledCount = data.filter(row => {
    const response = row.contract_cancelled;
    return response && response.toLowerCase().includes('yes');
  }).length;
  
  const cancelledPercent = totalOrganizations > 0 ? (cancelledCount / totalOrganizations) * 100 : 0;
  const formattedCancelledPercent = cancelledPercent.toFixed(1) + '%';
  
  // Calculate total staff laid off
  const totalStaffLaidOff = data.reduce((sum, row) => {
    const staffStr = row.staff_layoffs;
    if (!staffStr || staffStr === 'Unknown' || staffStr === '') return sum;
    
    const numbers = staffStr.match(/\d+/g);
    if (!numbers) return sum;
    
    const val = parseInt(numbers[0]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  
  const formattedStaffLaidOff = totalStaffLaidOff.toLocaleString('en-US');
  
  return {
    totalOrganizations,
    totalLostFunding: formattedFunding,
    cancelledContractsPercent: formattedCancelledPercent,
    totalStaffLaidOff: formattedStaffLaidOff,
  };
}

function processNormalizedOrganizationTypes(data: NormalizedSurveyData[]): ChartData[] {
  const orgTypeCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.org_type && row.org_type !== 'Unknown') {
      // Split multiple org types and count each
      const orgTypes = row.org_type.split('; ').map(type => type.trim());
      orgTypes.forEach(type => {
        orgTypeCounts[type] = (orgTypeCounts[type] || 0) + 1;
      });
    }
  });
  
  return Object.entries(orgTypeCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([type, count]) => ({ type, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedServicesProvided(data: NormalizedSurveyData[]): ChartData[] {
  const serviceCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.services_provided && row.services_provided !== 'None') {
      // Split multiple services and count each
      const services = row.services_provided.split('; ').map(service => service.trim());
      services.forEach(service => {
        serviceCounts[service] = (serviceCounts[service] || 0) + 1;
      });
    }
  });
  
  return Object.entries(serviceCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([service, count]) => ({ type: service, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedOptimismByOrgType(data: NormalizedSurveyData[]): OptimismData[] {
  const optimismLevels = ['Very optimistic', 'Optimistic', 'Neutral', 'Not optimistic', 'Not at all Optimistic'];
  const orgTypeOptimism: { [key: string]: { [key: string]: number } } = {};
  
  data.forEach(row => {
    if (row.org_type && row.org_type !== 'Unknown') {
      const orgTypes = row.org_type.split('; ').map(type => type.trim());
      const optimism18mo = row.optimism_18mo;
      
      orgTypes.forEach(orgType => {
        if (!orgTypeOptimism[orgType]) {
          orgTypeOptimism[orgType] = {};
          optimismLevels.forEach(level => {
            orgTypeOptimism[orgType][level] = 0;
          });
        }
        
        if (optimism18mo && optimismLevels.includes(optimism18mo)) {
          orgTypeOptimism[orgType][optimism18mo]++;
        }
      });
    }
  });
  
  return Object.entries(orgTypeOptimism)
    .filter(([_, optimismCounts]) => {
      // Only include categories that have any optimism responses
      return Object.values(optimismCounts).some(count => count > 0);
    })
    .map(([orgType, optimismCounts]) => ({
      group: orgType,
      values: optimismLevels.map(level => optimismCounts[level] || 0)
    }))
    .sort((a, b) => {
      const aTotal = a.values.reduce((sum, val) => sum + val, 0);
      const bTotal = b.values.reduce((sum, val) => sum + val, 0);
      return bTotal - aTotal;
    });
}

function processNormalizedOptimism18mo(data: NormalizedSurveyData[]): ChartData[] {
  const optimismLevels = ['Very optimistic', 'Optimistic', 'Neutral', 'Not optimistic', 'Not at all Optimistic'];
  const optimismCounts: { [key: string]: number } = {};
  
  // Initialize counts
  optimismLevels.forEach(level => {
    optimismCounts[level] = 0;
  });
  
  data.forEach(row => {
    if (row.optimism_18mo && optimismLevels.includes(row.optimism_18mo)) {
      optimismCounts[row.optimism_18mo]++;
    }
  });
  
  return Object.entries(optimismCounts)
    .filter(([_, count]) => count > 0)
    .map(([level, count]) => ({ type: level, value: count }))
    .sort((a, b) => {
      const aIndex = optimismLevels.indexOf(a.type);
      const bIndex = optimismLevels.indexOf(b.type);
      return aIndex - bIndex; // Sort by optimism level order
    });
}

function processNormalizedOptimism5yr(data: NormalizedSurveyData[]): ChartData[] {
  const optimismLevels = ['Very optimistic', 'Optimistic', 'Neutral', 'Not optimistic', 'Not at all Optimistic'];
  const optimismCounts: { [key: string]: number } = {};
  
  // Initialize counts
  optimismLevels.forEach(level => {
    optimismCounts[level] = 0;
  });
  
  data.forEach(row => {
    if (row.optimism_5yr && optimismLevels.includes(row.optimism_5yr)) {
      optimismCounts[row.optimism_5yr]++;
    }
  });
  
  return Object.entries(optimismCounts)
    .filter(([_, count]) => count > 0)
    .map(([level, count]) => ({ type: level, value: count }))
    .sort((a, b) => {
      const aIndex = optimismLevels.indexOf(a.type);
      const bIndex = optimismLevels.indexOf(b.type);
      return aIndex - bIndex; // Sort by optimism level order
    });
}

function processNormalizedIndirectEffects(data: NormalizedSurveyData[]): ChartData[] {
  const effectCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.indirect_effects && row.indirect_effects !== 'None') {
      // Split multiple effects and count each
      const effects = row.indirect_effects.split('; ').map(effect => effect.trim());
      effects.forEach(effect => {
        effectCounts[effect] = (effectCounts[effect] || 0) + 1;
      });
    }
  });
  
  return Object.entries(effectCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([effect, count]) => ({ type: effect, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedBudgetReduction(data: NormalizedSurveyData[]): ChartData[] {
  const budgetResponses: ChartData[] = [];
  
  data.forEach(row => {
    if (row.pct_budget_reduced && row.pct_budget_reduced !== 'Unknown' && row.pct_budget_reduced !== '') {
      // Ensure it's a string before calling trim()
      const budgetReduction = String(row.pct_budget_reduced).trim();
      // Extract the percentage value for sorting
      const percentageMatch = budgetReduction.match(/(\d+)/);
      const percentageValue = percentageMatch ? parseInt(percentageMatch[1]) : 0;
      
      budgetResponses.push({ 
        type: budgetReduction, 
        value: percentageValue 
      });
    }
  });
  
  return budgetResponses.sort((a, b) => a.value - b.value);
}

function processNormalizedAIUsage(data: NormalizedSurveyData[]): ChartData[] {
  const aiUsageCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.ai_use && row.ai_use !== 'None') {
      // Split multiple AI usages and count each
      const aiUsages = row.ai_use.split('; ').map(usage => usage.trim());
      aiUsages.forEach(usage => {
        aiUsageCounts[usage] = (aiUsageCounts[usage] || 0) + 1;
      });
    }
  });
  
  return Object.entries(aiUsageCounts)
    .map(([usage, count]) => ({ type: usage, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedTechnologyAdoption(data: NormalizedSurveyData[]): ChartData[] {
  const techCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.new_tech && row.new_tech !== 'None') {
      // Split multiple technologies and count each
      const technologies = row.new_tech.split('; ').map(tech => tech.trim());
      technologies.forEach(tech => {
        techCounts[tech] = (techCounts[tech] || 0) + 1;
      });
    }
  });
  
  return Object.entries(techCounts)
    .map(([tech, count]) => ({ type: tech, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedServiceImpacts(data: NormalizedSurveyData[]): ChartData[] {
  const impactCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.service_impacts && row.service_impacts !== 'None') {
      // Split multiple impacts and count each
      const impacts = row.service_impacts.split('; ').map(impact => impact.trim());
      impacts.forEach(impact => {
        impactCounts[impact] = (impactCounts[impact] || 0) + 1;
      });
    }
  });
  
  return Object.entries(impactCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([impact, count]) => ({ type: impact, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedAdaptations(data: NormalizedSurveyData[]): ChartData[] {
  const adaptationCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.adaptations && row.adaptations !== 'None') {
      // Split multiple adaptations and count each
      const adaptations = row.adaptations.split('; ').map(adaptation => adaptation.trim());
      adaptations.forEach(adaptation => {
        adaptationCounts[adaptation] = (adaptationCounts[adaptation] || 0) + 1;
      });
    }
  });
  
  return Object.entries(adaptationCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([adaptation, count]) => ({ type: adaptation, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedTechBarriers(data: NormalizedSurveyData[]): ChartData[] {
  const barrierCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.tech_barriers && row.tech_barriers !== 'None') {
      // Split multiple barriers and count each
      const barriers = row.tech_barriers.split('; ').map(barrier => barrier.trim());
      barriers.forEach(barrier => {
        barrierCounts[barrier] = (barrierCounts[barrier] || 0) + 1;
      });
    }
  });
  
  return Object.entries(barrierCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([barrier, count]) => ({ type: barrier, value: count }))
    .sort((a, b) => b.value - a.value);
}

function processNormalizedFundingAssociatedAgencies(data: NormalizedSurveyData[]): ChartData[] {
  const agencyCounts: { [key: string]: number } = {};
  
  data.forEach(row => {
    if (row.funding_associated_agencies && row.funding_associated_agencies !== 'None') {
      // Split multiple agencies and count each
      const agencies = row.funding_associated_agencies.split('; ').map(agency => agency.trim());
      agencies.forEach(agency => {
        agencyCounts[agency] = (agencyCounts[agency] || 0) + 1;
      });
    }
  });
  
  return Object.entries(agencyCounts)
    .filter(([_, count]) => count > 0) // Only include categories with responses
    .map(([agency, count]) => ({ type: agency, value: count }))
    .sort((a, b) => b.value - a.value);
} 